import { css } from "@emotion/css";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { Callout, EmptyStateNoResults, Pagination, SearchInput, SimpleDataTable, Tooltip, CheckboxNew, accessibilityStyles } from "@octopusdeploy/design-system-components";
import { ArrowDownToSquareIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { FeedResource, PackageVersionResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import DeleteDialog from "~/components/Dialog/DeleteDialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import InternalLink from "~/components/Navigation/InternalLink";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Text } from "~/components/form";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { useSelections } from "~/hooks/useSelections";
import ByteSizeFormatter from "~/utils/ByteSizeFormatter";
import DateFormatter from "~/utils/DateFormatter";
interface PackageVersionsPageProps {
    loaderData: PackageVersionsPageLoaderData;
    queryParams: PackageVersionsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<PackageVersionsPageQueryParams>;
    spaceId: string;
    packageId: string;
}
interface PackageVersionsPageState {
    isSure: boolean;
    confirmationInputValue: string;
}
export interface PackageVersionsPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    filter: string;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export async function packageVersionsPageLoader(repository: Repository, packageId: string, queryParams: PackageVersionsPageQueryParams): Promise<PackageVersionsPageLoaderData> {
    const feed = await repository.Feeds.getBuiltIn();
    const packages = getPaginatedPackageVersions(repository, feed, packageId, queryParams.page, queryParams.pageSize, queryParams.filter);
    const packageVersionStats = getPackageVersionStats(repository, feed, packageId);
    return { packages: await packages, feed, hasPackageVersions: (await packageVersionStats).TotalResults > 0 };
}
async function getPackageVersionStats(repository: Repository, feed: FeedResource, packageId: string) {
    return repository.Feeds.searchPackageVersions(feed, packageId, { skip: 0, take: 0 });
}
async function getPaginatedPackageVersions(repository: Repository, feed: FeedResource, packageId: string, page: number = 1, pageSize: number = defaultPageSize, filter: string = "") {
    return repository.Feeds.searchPackageVersions(feed, packageId, {
        skip: (page - 1) * pageSize,
        take: pageSize,
        filter,
    });
}
function usePaginatedPackageVersionsQuery(initialResult: PackageVersionsPageLoaderData, packageId: string, queryParams: PackageVersionsPageQueryParams) {
    const { page, pageSize, filter } = queryParams;
    const { feed } = initialResult;
    return useQuery((repository) => getPaginatedPackageVersions(repository, feed, packageId, page, pageSize, filter), [feed, packageId, page, pageSize, filter], "Get paginated packages", { initialResult: initialResult.packages });
}
interface PackageVersionsPageLoaderData {
    packages: ResourceCollection<PackageVersionResource>;
    feed: FeedResource;
    hasPackageVersions: boolean;
}
export function PackageVersionsPage({ spaceId, packageId, queryParams, setQueryParams, loaderData }: PackageVersionsPageProps) {
    const { hasPackageVersions } = loaderData;
    const deleteDialogControls = useDialogTrigger();
    const navigation = useSpaceAwareNavigation();
    const [debouncedFilter, setDebouncedFilter] = useDebouncedExternalState(queryParams.filter, (value) => setQueryParams({ filter: value, page: 1 }), 200);
    const isFeedAdmin = isAllowed({ permission: Permission.BuiltInFeedAdminister, project: "*" });
    const { result: paginatedPackageVersions, refetch } = usePaginatedPackageVersionsQuery(loaderData, packageId, queryParams);
    const { isAllSelected, isSelected, toggleSelectAll, toggleSelection, selections, clearSelections } = useSelections(paginatedPackageVersions.Items, (item) => item.Id);
    const multipleSelected = selections.length > 1;
    const isSelectAllIndeterminate = selections.length > 0 && !isAllSelected;
    React.useEffect(() => {
        clearSelections();
    }, [queryParams, clearSelections]);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const { execute: deleteSelectedPackageVersions, isExecuting: isBusyDeleting } = useMutation({
        name: "Delete selected package versions",
        action: async (repository) => {
            await repository.Packages.deleteMany(selections.map((x) => x.Id));
            const newPageNumber = selections.length === paginatedPackageVersions.Items.length ? clamp((queryParams.page ?? 1) - 1, { min: 1 }) : (queryParams.page ?? 1);
            const stillHasPackages = (await getPackageVersionStats(repository, loaderData.feed, packageId)).TotalResults > 0;
            if (!stillHasPackages) {
                navigation.redirect(links.builtInPackageRepositoryPage.generateUrl({ spaceId }));
            }
            if (newPageNumber === (queryParams.page ?? 1)) {
                clearSelections();
                refetch();
            }
            else {
                onPageChange(newPageNumber);
            }
        },
    });
    const [deletionConfirmation, setDeletionConfirmation] = React.useState<PackageVersionsPageState>({
        isSure: false,
        confirmationInputValue: "",
    });
    function confirmationOnChange(value: string) {
        let isSure = false;
        if (value === "Delete") {
            isSure = true;
        }
        setDeletionConfirmation((prev) => ({ ...prev, confirmationInputValue: value, isSure }));
    }
    React.useEffect(() => {
        if (!hasPackageVersions) {
            navigation.redirect(links.builtInPackageRepositoryPage.generateUrl({ spaceId }));
        }
    }, [navigation, hasPackageVersions, spaceId]);
    function renderDeleteDialog() {
        const selectedPackageCount = selections.length;
        const message = `Are you sure you want to delete ${multipleSelected ? `these ${selectedPackageCount}` : "this"} package${multipleSelected ? "s" : ""}?`;
        const isSure = deletionConfirmation.isSure;
        return (<DeleteDialog deleteButtonDisabled={!isSure} title={message} open={deleteDialogControls.isOpen} onClose={() => {
                deleteDialogControls.closeAllDialogs();
                setDeletionConfirmation((prev) => ({ ...prev, confirmationInputValue: "", isSure: false }));
            }} onDeleteClick={deleteSelectedPackageVersions} renderContent={() => (<>
                        <Callout title="This is a destructive action" type={"danger"}>
                            This action <strong>cannot</strong> be undone. This will permanently delete files from disk and break releases relying on {multipleSelected ? "these" : "this"} package{multipleSelected ? "s" : ""}.
                        </Callout>
                        <div>
                            <p>
                                Please type in <strong>Delete</strong> to confirm.
                            </p>
                            <Text accessibleName={"Delete Confirmation"} value={deletionConfirmation.confirmationInputValue} onChange={confirmationOnChange}/>
                        </div>
                    </>)}/>);
    }
    const sideBar = <p>If any projects depend on these packages, they will not be able to be deployed should you delete required versions from the repository.</p>;
    const hideBottomPagination = paginatedPackageVersions.Items.length < 20;
    return (<PageContent header={{
            title: packageId,
            breadcrumbs: [{ label: "Packages", pageUrl: links.builtInPackageRepositoryPage.generateUrl({ spaceId }) }],
            pageActions: [getDeletePageAction(selections.length, deleteDialogControls.openDialog, isBusyDeleting)],
        }} filters={{ inputs: [<SearchInput value={debouncedFilter} onChange={setDebouncedFilter} accessibleName={"Search by package version"} placeholder={"Search by version"}/>] }} pagination={{
            ui: (<Pagination label={"Package Versions"} totalResults={paginatedPackageVersions.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={paginatedPackageVersions.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} sidebar={sideBar}>
            {renderDeleteDialog()}
            {paginatedPackageVersions.TotalResults === 0 ? (<EmptyStateNoResults />) : (<SimpleDataTable data={paginatedPackageVersions.Items} getRowKey={(pkg) => pkg.Id} columns={[
                {
                    title: {
                        columnKey: "Select All",
                        render: () => <CheckboxNew ariaLabel={"Select all package versions"} disabled={!isFeedAdmin} value={isAllSelected} isIndeterminate={isSelectAllIndeterminate} onChange={toggleSelectAll}/>,
                    },
                    render: (resource) => (<CheckboxNew ariaLabel={isSelected(resource) ? `Unselect version ${resource.Version}` : `Select version ${resource.Version}`} disabled={!isFeedAdmin} value={isSelected(resource)} onChange={() => toggleSelection(resource)}/>),
                },
                { title: "Version", render: (pkg) => <PackageVersionCell pkg={pkg} spaceId={spaceId}/> },
                { title: "Published", render: (pkg) => <PackageVersionPublishedDateCell pkg={pkg}/> },
                { title: "Size", render: (pkg) => <PackageVersionSizeCell pkg={pkg}/> },
            ]}/>)}
        </PageContent>);
}
function getDeletePageAction(selectedPackageCount: number, onDelete: () => void, isBusy: boolean): PageAction {
    return {
        type: "button",
        buttonType: "destructive",
        hasPermissions: isAllowed({ permission: Permission.BuiltInFeedAdminister, project: "*" }),
        label: "Delete selected",
        disabled: isBusy || selectedPackageCount === 0,
        onClick: onDelete,
    };
}
function PackageVersionCell({ spaceId, pkg }: {
    spaceId: string;
    pkg: PackageVersionResource;
}) {
    return (<div className={packageVersionCellStyles}>
            <PermissionCheck permission={Permission.BuiltInFeedDownload} project="*">
                <a href={pkg.Links["Raw"]}>
                    <ArrowDownToSquareIcon size={"font-size"}/>{" "}
                    <span className={downloadPackageAccessibilityStyles}>
                        Download package {pkg.PackageId} version {pkg.Version}
                    </span>
                </a>
                &nbsp;
            </PermissionCheck>
            <InternalLink accessibleName={`View package details for ${pkg.PackageId} version ${pkg.Version}`} to={links.packageDetailsPage.generateUrl({ spaceId, id: pkg.Id })}>
                {pkg.Version}
            </InternalLink>
        </div>);
}
const packageVersionCellStyles = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: space[2],
});
const downloadPackageAccessibilityStyles = css({
    ...accessibilityStyles.hidden,
});
function PackageVersionPublishedDateCell({ pkg }: {
    pkg: PackageVersionResource;
}) {
    return <Tooltip content={DateFormatter.dateToLongFormat(pkg.Published)}>{DateFormatter.dateToShortFormat(pkg.Published)}</Tooltip>;
}
function PackageVersionSizeCell({ pkg }: {
    pkg: PackageVersionResource;
}) {
    const pkgSizeInBytes = ByteSizeFormatter(pkg.SizeBytes);
    return <>{!!pkgSizeInBytes ? pkgSizeInBytes : null}</>;
}
function clamp(value: number, options: {
    min?: number;
    max?: number;
}) {
    const { min, max } = options;
    if (min && value < min) {
        return min;
    }
    if (max && value > max) {
        return max;
    }
    return value;
}
